import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { DataTableColumn, PaginatorStatus, SortConfiguration } from '../../generics/data-table/data-table.component';
import { ErrorResponse } from 'src/app/models/error.model';
import { Cobertura, TipoProducto } from 'src/app/models/cobertura.model';
import { Zona } from 'src/app/models/zona.model';
import { CoberturasService } from 'src/app/services/coberturas.service';
import { ZonasService } from 'src/app/services/zonas.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
    selector: 'app-coberturas',
    templateUrl: './coberturas.component.html',
    styleUrls: ['./coberturas.component.scss']
})
export class CoberturasComponent implements OnInit, OnChanges {

    @Input() embedded = false;
    @Input() tipoProductoInicial?: TipoProducto;

    @ViewChild('zonaColumnTemplate', { static: true })
    zonaColumnTemplate: TemplateRef<any>;

    @ViewChild('activoColumnTemplate', { static: true })
    activoColumnTemplate: TemplateRef<any>;

    @ViewChild('actionsColumnTemplate', { static: true })
    actionsColumnTemplate: TemplateRef<any>;

    paginatorStatus: PaginatorStatus = {
        itemsPerPage: 50,
        page: 0,
        totalElementCount: 0
    };

    columns: DataTableColumn[] = [
        { name: 'Servicio', prop: 'nombre', sortable: true },
        { name: '%', prop: 'porcentaje', width: '70px', parseValue: (v: number) => `${v}%` },
        { name: 'Mín (ARS)', prop: 'topeMinimo', width: '110px', parseValue: (v: number) => v.toLocaleString('es-AR') },
        { name: 'Máx (ARS)', prop: 'topeMaximo', width: '110px', parseValue: (v: number) => v.toLocaleString('es-AR') },
        { name: 'Zona', width: '120px' },
        { name: 'Estado', width: '90px' },
        { name: '', width: '90px' }
    ];

    tipoProductoActual: TipoProducto = 'VIVIENDA';
    selectedZonaId: number | null = null;
    sortConfiguration?: SortConfiguration;
    coberturas: Cobertura[] = [];
    zonas: Zona[] = [];

    get displayCoberturas(): (Cobertura & { heredado?: boolean })[] {
        if (this.selectedZonaId === null) {
            return this.coberturas.filter(c => !c.idZona);
        }
        const defaults = this.coberturas.filter(c => !c.idZona && c.activo);
        const zoneItems = this.coberturas.filter(c => c.idZona === this.selectedZonaId && c.activo);
        const merged = defaults.map(def => {
            const override = zoneItems.find(z => z.nombre === def.nombre);
            return override ?? { ...def, heredado: true };
        });
        const orphans = zoneItems.filter(zi => !defaults.find(d => d.nombre === zi.nombre));
        return [...merged, ...orphans];
    }

    constructor(
        private coberturasService: CoberturasService,
        private zonasService: ZonasService,
        private utilsService: UtilsService
    ) { }

    ngOnInit(): void {
        if (this.tipoProductoInicial) {
            this.tipoProductoActual = this.tipoProductoInicial;
        }
        this.columns.find(col => col.name === 'Zona').template = this.zonaColumnTemplate;
        this.columns.find(col => col.name === 'Estado').template = this.activoColumnTemplate;
        this.columns.find(col => col.name === '').template = this.actionsColumnTemplate;
        this.loadZonas();
        this.getCoberturas();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tipoProductoInicial'] && !changes['tipoProductoInicial'].firstChange) {
            this.tipoProductoActual = this.tipoProductoInicial;
            this.selectedZonaId = null;
            this.paginatorStatus.page = 0;
            this.getCoberturas();
        }
    }

    loadZonas() {
        this.zonasService.getZonas(0, 200, [], { 'ocultar-inactivos': 'false' }).subscribe({
            next: response => { this.zonas = response.body ?? []; }
        });
    }

    getZonaLabel(idZona?: number): string {
        if (!idZona) return 'Default';
        return this.zonas.find(z => z.id === idZona)?.nombre ?? `Zona ${idZona}`;
    }

    getCoberturas() {
        this.coberturasService.getCoberturas(
            this.paginatorStatus.page,
            this.paginatorStatus.itemsPerPage,
            this.sortConfiguration ? [this.sortConfiguration] : [],
            this.tipoProductoActual,
            false
        ).subscribe({
            next: response => {
                this.paginatorStatus.totalElementCount = parseInt(response.headers.get('pagination-total-count') ?? '0', 10);
                this.coberturas = response.body;
            },
            error: (error: ErrorResponse) => this.utilsService.alert('Error al obtener coberturas', error.error?.descripcion)
        });
    }

    onTipoChange(tipo: TipoProducto) {
        this.tipoProductoActual = tipo;
        this.paginatorStatus.page = 0;
        this.getCoberturas();
    }

    onZonaChange(idZona: number | null) {
        this.selectedZonaId = idZona;
    }

    revertirADefault(cobertura: Cobertura) {
        const dialog = this.utilsService.confirm(
            '¿Revertir esta cobertura al Default?',
            `"${cobertura.nombre}" pasará a la configuración base (sin zona específica).`
        );
        dialog.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.coberturasService.updateCobertura({ ...cobertura, idZona: null }).subscribe({
                    next: () => {
                        this.utilsService.alert('Cobertura revertida', `"${cobertura.nombre}" ahora pertenece al Default.`);
                        this.getCoberturas();
                    },
                    error: (error: ErrorResponse) => this.utilsService.alert('Error al revertir', error.error?.descripcion)
                });
            }
        });
    }

    updatePage(event: PageEvent) {
        this.paginatorStatus.itemsPerPage = event.pageSize;
        this.paginatorStatus.page = event.pageIndex;
        this.getCoberturas();
    }

    updateSort(event: SortConfiguration) {
        this.sortConfiguration = event;
        this.getCoberturas();
    }

    deleteCobertura(cobertura: Cobertura) {
        const dialog = this.utilsService.confirm(
            '¿Dar de baja esta cobertura?',
            `La cobertura "${cobertura.nombre}" quedará inactiva.`
        );
        dialog.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.coberturasService.deleteCobertura(cobertura.id).subscribe({
                    next: () => {
                        this.utilsService.alert('Cobertura dada de baja', `"${cobertura.nombre}" fue dada de baja correctamente.`);
                        this.getCoberturas();
                    },
                    error: (error: ErrorResponse) => this.utilsService.alert('Error al dar de baja', error.error?.descripcion)
                });
            }
        });
    }
}
